import {
  // Home, // Dashboard temporarily disabled
  UsersRound,
  Package,
  Puzzle,
  BarChart3,
  Gauge,
  Settings,
  FileText,
  FlaskConical,
  Database,
  Network,
  Building2,
  Bot,
  Sparkles,
  ContactRound,
  ClipboardCheck,
  Newspaper,
  Factory,
  Workflow,
  SlidersHorizontal,
  Globe,
  KanbanSquare,
  Search,
  MessageCircleQuestion,
  MessagesSquare,
  LucideIcon,
} from "lucide-react";

export interface MenuItem {
  title: string;
  label?: string;
  icon: LucideIcon;
  variant: "default" | "ghost";
  target: string;
  /** Run this action on click instead of navigating to `target`. */
  action?: "archiveSearch";
  visible: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  staffOnly?: boolean;
  // Marks this item as the one that carries the Neptune live-connection dot
  // (green pulsing / grey). The actual online state is live subscription
  // state, not something derivable from MenuItemParams, so Nav fills it in.
  statusDot?: boolean;
  children?: MenuItem[];
}

interface MenuItemParams {
  userRoles: {
    isAdmin: boolean;
    isLytraStaff: boolean;
    isAgent: boolean;
  };
  features: {
    dashboard: boolean;
    assistantChat: boolean;
    liveChat: boolean;
    calls: boolean;
    documents: boolean;
    assistantChatEnabled: boolean;
    dbTables: boolean;
    serviceAssistant: boolean;
    ontologyLookup: boolean;
    usageStatistics: boolean;
    serviceKpis: boolean;
    ticketing: boolean;
    customerChat: boolean;
  };
}

interface MenuItemConfig {
  title: string;
  icon: LucideIcon;
  variant: "default" | "ghost";
  target: string;
  action?: "archiveSearch";
  staffOnly?: boolean | ((params: MenuItemParams) => boolean);
  visible: (params: MenuItemParams) => boolean;
  disabled?: (params: MenuItemParams) => boolean;
  disabledTooltip?: string;
  statusDot?: boolean;
  children?: MenuItemConfig[];
}

const topMenuItems: MenuItemConfig[] = [
  // Dashboard temporarily disabled due to heavy queries causing backend overload
  // {
  //   title: "Dashboard",
  //   icon: Home,
  //   variant: "ghost",
  //   target: "/dashboard",
  //   visible: ({ userRoles, features }) =>
  //     userRoles.isLytraStaff || features.dashboard,
  // },
  {
    // #958: the first thing a team with the ticket system sees after login, where
    // work piles up per stage and team.
    title: "nav.board",
    icon: KanbanSquare,
    variant: "ghost",
    target: "/board",
    visible: ({ features }) => features.ticketing,
  },
  {
    title: "nav.chat",
    icon: Globe,
    variant: "ghost",
    target: "/agent/general",
    visible: ({ features }) => features.serviceAssistant,
    // #421: the chat is an agent tool. Admins without the agent role see the
    // item locked instead of a working chat (isAgent is true for lytra_staff,
    // the backend bypasses all checks for them).
    disabled: ({ userRoles }) => !userRoles.isAgent,
    disabledTooltip: "nav.serviceAssistantLocked",
  },
  {
    // The Neptune-synced ticket view (formerly the "Ausgewählt" tab in
    // AgentTabBar) — same visibility/lock rules as Chat above.
    title: "nav.serviceAssistant",
    icon: Bot,
    variant: "ghost",
    target: "/agent/portal",
    visible: ({ features }) => features.serviceAssistant,
    disabled: ({ userRoles }) => !userRoles.isAgent,
    disabledTooltip: "nav.serviceAssistantLocked",
    statusDot: true,
  },
  {
    // The tenant's customer chat card, live, with its config (name, images,
    // colours, texts, fonts), kept apart from the service agents' own chat above.
    // Staff only for now, even where the tenant has the feature.
    title: "nav.customerChat",
    icon: MessageCircleQuestion,
    variant: "ghost",
    target: "/agent/customer-chat",
    visible: ({ userRoles, features }) => userRoles.isLytraStaff && features.customerChat,
    staffOnly: true,
    disabled: ({ userRoles }) => !userRoles.isAgent,
    disabledTooltip: "nav.serviceAssistantLocked",
  },
  {
    // Every customer chat of the tenant, to read what customers asked and got.
    title: "nav.customerChats",
    icon: MessagesSquare,
    variant: "ghost",
    target: "/agent/customer-chats",
    visible: ({ userRoles, features }) => userRoles.isLytraStaff && features.customerChat,
    staffOnly: true,
  },
  {
    // Opens the archive search as a quick-search palette instead of a page.
    // Sits next to the assistant because it serves the same people: the
    // assistant answers a question, the search re-finds one specific past
    // message. Stays highlighted while a found conversation is open under
    // /archive. Locked for users without the agent role, like the chat.
    title: "nav.search",
    icon: Search,
    variant: "ghost",
    target: "/archive",
    action: "archiveSearch",
    // The search belongs to the conversation browser (feature.ticketing), the
    // replacement for the ticket-system flag. Staff always get it, marked as
    // staff-only while the tenant itself does not have the browser.
    visible: ({ userRoles, features }) => userRoles.isLytraStaff || features.ticketing,
    staffOnly: ({ userRoles, features }) => userRoles.isLytraStaff && !features.ticketing,
    disabled: ({ userRoles }) => !userRoles.isAgent,
    disabledTooltip: "nav.serviceAssistantLocked",
  },
  {
    title: "nav.customers",
    icon: ContactRound,
    variant: "ghost",
    target: "/contacts",
    visible: ({ userRoles }) => userRoles.isLytraStaff,
    staffOnly: true,
  },

  {
    title: "nav.products",
    icon: Package,
    variant: "ghost",
    target: "/products",
    visible: () => false, // Hidden for now
  },
  {
    title: "nav.spareParts",
    icon: Puzzle,
    variant: "ghost",
    target: "/replacement_parts",
    visible: () => false, // Hidden for now
  },
  {
    // Staff always; tenant admins once the tenant has the feature. Agents never:
    // these numbers describe the team, not the individual.
    title: "nav.serviceKpis",
    icon: Gauge,
    variant: "ghost",
    target: "/service-kpis",
    visible: ({ userRoles, features }) =>
      userRoles.isLytraStaff || (userRoles.isAdmin && features.serviceKpis),
    staffOnly: ({ userRoles, features }) =>
      userRoles.isLytraStaff && !features.serviceKpis,
  },
  {
    title: "nav.ontology",
    icon: Network,
    variant: "ghost",
    target: "/ontology",
    visible: ({ userRoles }) => userRoles.isLytraStaff,
    staffOnly: true,
    // Maschinenpark and Antwort-Steuerung are tenant-admin features now and sit in
    // the customer section above; they are deliberately not repeated here.
    children: [
      {
        title: "nav.ontologyPipeline",
        icon: Workflow,
        variant: "ghost",
        target: "/ontology/pipeline",
        visible: ({ userRoles }) => userRoles.isLytraStaff,
        staffOnly: true,
      },
      {
        title: "nav.knowledgeCheck",
        icon: ClipboardCheck,
        variant: "ghost",
        target: "/knowledge-check",
        visible: ({ userRoles }) => userRoles.isLytraStaff,
        staffOnly: true,
      },
    ],
  },
  {
    title: "nav.evaluation",
    icon: FlaskConical,
    variant: "ghost",
    target: "/eval",
    visible: ({ userRoles }) => userRoles.isLytraStaff,
    staffOnly: true,
  },
  {
    title: "nav.reranker",
    icon: Sparkles,
    variant: "ghost",
    target: "/reranker",
    visible: ({ userRoles }) => userRoles.isLytraStaff,
    staffOnly: true,
  },
  {
    title: "nav.changelog",
    icon: Newspaper,
    variant: "ghost",
    target: "/changelog",
    visible: ({ userRoles }) => userRoles.isLytraStaff,
    staffOnly: true,
  },
];

const bottomMenuItems: MenuItemConfig[] = [
  {
    title: "nav.tenantOverview",
    icon: Building2,
    variant: "ghost",
    target: "/tenant-overview",
    visible: ({ userRoles }) => userRoles.isLytraStaff,
    staffOnly: true,
  },
  {
    // The prescriptive-knowledge page (also at /ontology/prescriptive). This
    // route/label is how tenant admins reach it: control over how the AI
    // answers (mined SOPs they can review and adjust), not "ontology". Sits up
    // here with the tenant-facing items; staff use the same entry.
    title: "nav.aiControl",
    icon: SlidersHorizontal,
    variant: "ghost",
    target: "/antwort-steuerung",
    visible: ({ userRoles }) => userRoles.isAdmin || userRoles.isLytraStaff,
  },
  {
    // The customer's machine park: the machines they build and service, in the
    // structure the AI uses to scope answers. Tenant admins maintain it themselves;
    // the staff-only curation extras (document suggestions, backfills) sit behind
    // the staff role inside the page, not behind a second route.
    title: "nav.machinePark",
    icon: Factory,
    variant: "ghost",
    target: "/maschinenpark",
    visible: ({ userRoles }) => userRoles.isAdmin || userRoles.isLytraStaff,
  },
  {
    title: "nav.administration",
    icon: Settings,
    variant: "ghost",
    target: "/administration",
    visible: ({ userRoles }) => userRoles.isLytraStaff,
    staffOnly: true,
  },
  {
    title: "nav.team",
    icon: UsersRound,
    variant: "ghost",
    target: "/team",
    visible: ({ userRoles }) => userRoles.isAdmin || userRoles.isLytraStaff,
  },
  {
    title: "nav.documents",
    icon: FileText,
    variant: "ghost",
    target: "/documents",
    visible: ({ userRoles }) => userRoles.isLytraStaff || userRoles.isAdmin,
    disabled: ({ userRoles, features }) =>
      !userRoles.isLytraStaff && !features.documents,
    disabledTooltip: "nav.documentsNotConfigured",
  },
  {
    title: "nav.dbTables",
    icon: Database,
    variant: "ghost",
    target: "/db-tables",
    visible: ({ userRoles, features }) =>
      (userRoles.isAdmin || userRoles.isLytraStaff) && features.dbTables,
  },
  {
    title: "nav.billing",
    icon: BarChart3,
    variant: "ghost",
    target: "/usage",
    visible: ({ userRoles, features }) =>
      userRoles.isLytraStaff || (userRoles.isAdmin && features.usageStatistics),
  },
];

export function getMenuItems(params: MenuItemParams): {
  topLinks: MenuItem[];
  bottomLinks: MenuItem[];
} {
  const resolve = (item: MenuItemConfig): MenuItem => ({
    ...item,
    visible: item.visible(params),
    disabled: item.disabled ? item.disabled(params) : false,
    staffOnly:
      typeof item.staffOnly === "function"
        ? item.staffOnly(params)
        : (item.staffOnly ?? false),
    disabledTooltip: item.disabledTooltip,
    children: item.children?.map(resolve),
  });

  return {
    topLinks: topMenuItems.map(resolve),
    bottomLinks: bottomMenuItems.map(resolve),
  };
}

export function getFirstAvailableRoute(params: MenuItemParams): string {
  const { isAdmin, isLytraStaff, isAgent } = params.userRoles;
  if (!isAdmin && !isLytraStaff) {
    if (!isAgent) return "/not-enabled";
    if (!params.features.serviceAssistant && !params.features.ticketing) {
      return "/agent/general";
    }
  }

  const { topLinks, bottomLinks } = getMenuItems(params);
  const allLinks = [...topLinks, ...bottomLinks];

  // Items with an action (like the search palette) open something in place,
  // so they are never a landing route.
  const firstAvailable = allLinks.find(
    (link) => link.visible && !link.disabled && !link.action,
  );

  return firstAvailable?.target ?? "/settings";
}
